
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { salesService } from '../../services/salesService';
import { getApprovedUsers } from '../../services/firestore';
import { FiPlus, FiSearch, FiCalendar, FiDollarSign, FiImage, FiTrash2, FiZoomIn, FiX, FiCheckCircle, FiXCircle, FiFilter, FiUsers, FiCpu, FiAlertCircle, FiInfo } from 'react-icons/fi';
import toast from 'react-hot-toast';
import HxSelect from '../ui/HxSelect';
import { UserRound as LUser, Crown as LCrown } from 'lucide-react';
import { collection, query, where, orderBy, getDocs, addDoc, serverTimestamp, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../../services/firebase'; // Asegúrate de exportar storage
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { analyzeSaleEvidence, chatSaleAnalysis, superVendedorChat } from '../../services/gemini';
import confetti from 'canvas-confetti';
import RulesManagerModal from './RulesManagerModal';
import AiMemoryLogModal from './AiMemoryLogModal';
import { getCommissionRules, seedCommissionRules, getSalesLessons, addSalesLesson, saveSalesKnowledge, getSalesKnowledge } from '../../services/firestore';
import { FiSend, FiMessageSquare, FiTrendingUp, FiRefreshCw, FiUser } from 'react-icons/fi';

// --- Cálculo de comisión (fuente única de verdad) ---
// 1) Quitar IGV (precio / 1.18) -> Neto
// 2) Aplicar 30% de Impuesto a la Renta sobre el Neto -> Base comisionable
// 3) Sobre la base: 15% si precio > 100, si no 10% -> Comisión
const IMPUESTO_RENTA = 0.30;
export const computeCommission = (price) => {
    const p = parseFloat(price) || 0;
    const net = p / 1.18;                       // Neto sin IGV
    const base = net * (1 - IMPUESTO_RENTA);    // Base tras 30% Imp. Renta
    const rate = p > 100 ? 0.15 : 0.10;
    const commission = base * rate;
    return { net, base, rate, commission };
};

// Componente de Zoom Avanzado
const ImageZoomModal = ({ imageUrl, onClose }) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });

    // Al cambiar de imagen, reiniciar zoom/posición para revisar limpio
    useEffect(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, [imageUrl]);

    if (!imageUrl) return null;

    const handleWheel = (e) => {
        // Zoom con scroll. Multiplicativo para llegar rápido hasta 800%.
        e.preventDefault();
        const factor = e.deltaY > 0 ? 0.9 : 1.1;
        setScale(prev => Math.min(Math.max(prev * factor, 0.5), 8)); // 0.5x a 8x (800%)
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
        }
    };

    const handleMouseUp = () => setIsDragging(false);

    // Columna acoplada a la derecha (no tapa la tabla, conserva el scroll de la página)
    return (
        <div
            className="fixed top-0 right-0 h-full w-full md:w-[480px] z-50 bg-neutral-900 shadow-2xl border-l border-black/30 flex flex-col animate-fadeIn"
            onWheel={handleWheel}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-800 text-white shrink-0">
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <FiImage /> Evidencia
                    <span className="text-[11px] text-white/50 font-normal hidden sm:inline">· Scroll = zoom · Arrastrar = mover</span>
                </div>
                <button onClick={onClose} className="text-white/80 hover:text-white transition-transform hover:scale-110" title="Cerrar">
                    <FiX size={24} />
                </button>
            </div>

            {/* Imagen */}
            <div
                className="flex-1 overflow-hidden flex items-center justify-center cursor-move relative"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <img
                    src={imageUrl}
                    alt="Evidencia Zoom"
                    style={{
                        transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                    }}
                    className="max-w-none max-h-full object-contain select-none"
                    draggable={false}
                />
            </div>

            {/* Controles */}
            <div className="flex justify-center gap-3 bg-neutral-800 p-2 shrink-0">
                <button onClick={() => setScale(s => Math.max(s - 0.5, 0.5))} className="p-2 text-white hover:bg-white/10 rounded-full"><FiZoomIn className="transform rotate-180" /></button>
                <span className="text-white font-mono py-2 w-16 text-center">{Math.round(scale * 100)}%</span>
                <button onClick={() => setScale(s => Math.min(s + 0.5, 8))} className="p-2 text-white hover:bg-white/10 rounded-full"><FiZoomIn /></button>
                <button onClick={() => { setScale(1); setPosition({ x: 0, y: 0 }); }} className="px-3 text-white text-xs font-bold hover:bg-white/10 rounded-full" title="Restablecer">Reset</button>
                <button onClick={() => setScale(8)} className="px-3 text-white text-xs font-bold hover:bg-white/10 rounded-full" title="Zoom máximo 800%">800%</button>
            </div>
        </div>
    );
};

// Constantes de Reglas de Comisión
// Constantes de Reglas de Comisión
const COMMISSION_RULES = [
    {
        title: "🟢 Caso 2: Cliente indeciso + seguimiento",
        situation: "Cliente solicita información → el asesor realiza seguimiento (ej. 4 h) → el cliente paga.",
        result: "✅ Sí hay comisión",
        reason: "Venta lograda por seguimiento."
    },
    {
        title: "🟢 Caso 4: Atención fuera de horario",
        situation: "El cliente escribe fuera del horario regular (> 6:00 p. m.) y paga al momento.",
        result: "✅ Sí hay comisión",
        reason: "Atención extra-horaria. Excepción: Masterclass gratuitas."
    },
    {
        title: "🟢 Caso 5: Atención tardía y pago posterior",
        situation: "El asesor atiende fuera de horario (> 6:00 p. m.) y el cliente paga al día siguiente.",
        result: "✅ Sí hay comisión",
        reason: "Atención extra-horaria que impulsa la venta."
    },
    {
        title: "🟢 Caso 6: Seguimiento fuera de horario",
        situation: "El asesor realiza seguimiento después de las 6:00 p. m. y el cliente paga.",
        result: "✅ Sí hay comisión",
        reason: "Gestión comercial fuera de horario."
    },
    {
        title: "🟢 Caso 7: Dos asesores",
        situation: "Asesor A brinda información inicial. Asesor B realiza seguimiento y cierra la venta.",
        result: "✅ Comisión para B",
        reason: "El cierre se logró por seguimiento."
    },
    {
        title: "🟢 Caso 9: Retoma contacto tras seguimiento",
        situation: "Asesor realiza seguimiento, cliente no responde. Días después, cliente retoma y paga.",
        result: "✅ Sí hay comisión",
        reason: "Existió gestión comercial previa comprobable."
    },
    {
        title: "🟢 Caso 11: Masterclass + seguimiento",
        situation: "Cliente escribe por masterclass. Asesor hace seguimiento (incluso >3 días). Cliente paga.",
        result: "✅ Sí hay comisión",
        reason: "Pago como resultado directo del seguimiento."
    },
    {
        title: "🔴 Caso 1: Cliente decidido",
        situation: "El cliente pide información y paga al momento.",
        result: "❌ No hay comisión",
        reason: "Mera toma de pedido."
    },
    {
        title: "🔴 Caso 3: Sin seguimiento",
        situation: "El asesor no hace seguimiento y el cliente paga al día siguiente.",
        result: "❌ No hay comisión",
        reason: "Sin gestión activa."
    },
    {
        title: "🔴 Caso 8: Vuelve tras 7 días sin seguimiento",
        situation: "El cliente retoma contacto luego de una semana sin seguimiento del asesor.",
        result: "❌ No hay comisión",
        reason: "Falta de continuidad comercial."
    },
    {
        title: "🔴 Caso 10: Vuelve tras 48h sin seguimiento",
        situation: "Cliente deja de responder. Asesor no hace seguimiento. Cliente vuelve y paga a los 2 días.",
        result: "❌ No hay comisión",
        reason: "Solo atención básica obligatoria."
    }
];

// Componente Carousel de Reglas
const CommissionRulesWidget = ({ rules }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (!rules || rules.length === 0) return;
        const timer = setInterval(() => {
            // Asegurar que rules exista antes de acceder
            if (rules && rules.length > 0) {
                setCurrentIndex(prev => (prev + 1) % rules.length);
            }
        }, 5000);
        return () => clearInterval(timer);
    }, [rules]);

    // Si no hay reglas aún, no renderizar nada (pero después de los hooks)
    if (!rules || rules.length === 0) return null;

    const rule = rules[currentIndex];

    return (
        <>
            <div className="flex-1 bg-white border border-yellow-100 rounded-xl p-3 shadow-sm mx-4 relative overflow-hidden group cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setShowModal(true)}>
                <div className="absolute top-0 left-0 w-1 h-full bg-yellow-400"></div>

                <div className="ml-2 flex flex-col justify-center h-full">
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-bold text-yellow-600 uppercase tracking-wider flex items-center gap-1">
                            ℹ️ Reglas de Comisión ({currentIndex + 1}/{rules.length})
                        </span>
                        <span className="text-xs text-gray-400 group-hover:text-blue-500">Ver todas →</span>
                    </div>

                    <div className="animate-fadeIn">
                        <h4 className="text-sm font-bold text-gray-800 truncate">{rule.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${rule.result.includes('Sí') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {rule.result}
                            </span>
                            <span className="text-xs text-gray-500 truncate hidden xl:block">{rule.reason}</span>
                        </div>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="absolute bottom-0 left-0 h-0.5 bg-gray-100 w-full">
                    <div
                        className="h-full bg-yellow-400 transition-all duration-300 ease-linear"
                        style={{ width: `${((currentIndex + 1) / rules.length) * 100}% ` }}
                    ></div>
                </div>
            </div>

            {/* Modal de Reglas Completas */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">📜 Reglamento de Comisiones</h3>
                                <p className="text-sm text-gray-500">Guía para saber cuándo aplica comisión</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className="overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {rules.map((r, idx) => (
                                <div key={idx} className={`border rounded-xl p-4 ${r.result.includes('Sí') ? 'bg-green-50/50 border-green-100' : 'bg-red-50/50 border-red-100'}`}>
                                    <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                        {r.title}
                                    </h4>

                                    <div className="space-y-2 text-sm">
                                        <div className="bg-white/60 p-2 rounded-lg">
                                            <span className="font-semibold block text-xs uppercase text-gray-500 mb-0.5">Situación</span>
                                            {r.situation}
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="flex-1 bg-white/60 p-2 rounded-lg">
                                                <span className="font-semibold block text-xs uppercase text-gray-500 mb-0.5">Resultado</span>
                                                <span className={`font-bold ${r.result.includes('Sí') ? 'text-green-600' : 'text-red-600'}`}>{r.result}</span>
                                            </div>
                                            <div className="flex-1 bg-white/60 p-2 rounded-lg">
                                                <span className="font-semibold block text-xs uppercase text-gray-500 mb-0.5">Motivo</span>
                                                {r.reason}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="p-4 border-t border-gray-100 bg-gray-50 text-right">
                            <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-gray-900 text-white rounded-xl hover:bg-black transition-colors">
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Componente Custom Dropdown para Vendedores
const SellerSelect = ({ sellers, selectedSeller, onChange, currentUserId }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Encontrar el vendedor seleccionado
    // Si sellers es un array plano:
    const selectedObj = selectedSeller === 'all'
        ? null
        : sellers.find(s => s.id === selectedSeller);

    const handleSelect = (id) => {
        onChange(id);
        setIsOpen(false);
    }

    return (
        <div className="relative min-w-[240px]">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex items-center justify-between hover:border-blue-400 transition-colors shadow-sm text-left"
            >
                <div className="flex items-center gap-3 overflow-hidden">
                    {selectedSeller === 'all' ? (
                        <>
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                                <FiUsers size={16} />
                            </div>
                            <span className="text-sm font-medium text-gray-700 truncate">Todos los vendedores</span>
                        </>
                    ) : (
                        <>
                            {selectedObj?.photoURL ? (
                                <img src={selectedObj.photoURL} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold">
                                    {selectedObj?.displayName?.[0] || '?'}
                                </div>
                            )}
                            <div className="flex flex-col truncate">
                                <span className="text-sm font-bold text-gray-800 truncate">{selectedObj?.displayName || 'Usuario'}</span>
                            </div>
                        </>
                    )}
                </div>
                <div className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </div>
            </button>

            {isOpen && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-100 max-h-80 overflow-y-auto z-40 animate-fadeIn">
                    <div
                        onClick={() => handleSelect('all')}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-50"
                    >
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                            <FiUsers size={16} />
                        </div>
                        <span className="text-sm font-medium text-gray-700">Todos los vendedores</span>
                    </div>
                    {sellers.map((s) => (
                        <div
                            key={s.id}
                            onClick={() => handleSelect(s.id)}
                            className={`px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 ${selectedSeller === s.id ? 'bg-blue-50/50' : ''}`}
                        >
                            {s.photoURL ? (
                                <img src={s.photoURL} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold">
                                    {s.displayName?.[0] || 'U'}
                                </div>
                            )}
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-900">{s.displayName}</span>
                                <span className="text-xs text-gray-400">{s.email}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Backdrop para cerrar al hacer clic fuera */}
            {isOpen && <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)}></div>}
        </div>
    );
};

// Componente Modal de Nueva Venta

// Componente Modal de Nueva Venta
const NewSaleModal = ({ isOpen, onClose, onSave, isSaving, isAdmin, sellers, currentUserId }) => {
    const [formData, setFormData] = useState({
        studentName: '',
        phoneNumber: '',
        studentEmail: '', // Nuevo campo
        course: '',
        date: new Date().toISOString().split('T')[0], // Hoy por defecto
        price: '',
        firstPayment: '',
        evidenceFile: null,
        advisorId: currentUserId // Default al usuario actual
    });

    // Resetear advisorId cuando se abre el modal o cambian props
    useEffect(() => {
        if (isOpen) {
            // Si es admin, mantiene el user actual por defecto pero puede cambiar
            // Si NO es admin, forzamos su ID
            setFormData(prev => ({
                ...prev,
                advisorId: isAdmin ? (prev.advisorId || currentUserId) : currentUserId,
                date: new Date().toISOString().split('T')[0] // Reset date on open
            }));
        }
    }, [isOpen, currentUserId, isAdmin]);

    const [previewUrl, setPreviewUrl] = useState(null);

    // Cálculos en tiempo real
    const price = parseFloat(formData.price) || 0;
    const firstPayment = parseFloat(formData.firstPayment) || 0;
    const due = price - firstPayment;
    const { net, base, rate: commissionRate, commission } = computeCommission(price);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, evidenceFile: file }));
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.studentName || !formData.course || !formData.price || !formData.advisorId) {
            toast.error('Completa los campos obligatorios');
            return;
        }
        onSave(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-sans">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-fadeIn">
                <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800">Registrar Nueva Venta 🚀</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX size={24} /></button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">

                    {/* Selector de Vendedor (Solo Admin) o Info de Vendedor (Asesor) */}
                    <div>
                        {isAdmin ? (
                            <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
                                <label className="block text-xs font-bold text-orange-700 uppercase mb-1">Asignar Venta a:</label>
                                <HxSelect
                                    className="full"
                                    minWidth={0}
                                    value={formData.advisorId}
                                    onChange={v => handleChange({ target: { name: 'advisorId', value: v } })}
                                    options={[
                                        { value: currentUserId, label: 'Mí mismo (Admin)', icon: LCrown },
                                        ...sellers
                                            .filter(u => u.status === 'active' && u.id !== currentUserId) // Solo activos
                                            .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''))
                                            .map(u => ({ value: u.id, label: u.displayName || u.email, sub: u.displayName ? u.email : undefined, photo: u.photoURL || undefined, color: '#ea580c' })),
                                    ]}
                                    placeholder="Seleccionar vendedor…"
                                    searchPlaceholder="Buscar vendedor…"
                                    icon={LUser}
                                    ariaLabel="Asignar venta a"
                                />
                            </div>
                        ) : (
                            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex items-center gap-3">
                                {(() => {
                                    // Búsqueda robusta en el mapa (pasado como prop sellers si es diccionario, o buscar en array)
                                    // Para mantener compatibilidad con prop 'sellers' que suele ser array, buscamos:
                                    const currentUserInList = sellers.find(s => s.id === currentUserId);
                                    // Fallback visual si no se encuentra en la lista (pero el ID es correcto)
                                    const displayName = currentUserInList?.displayName || (currentUserId === currentUserId ? 'Yo' : 'Usuario');
                                    // Nota: Para acceder al user context aquí necesitaríamos pasarlo o usar un hook, pero currentUserId viene de prop.
                                    // Asumiendo que el componente padre pasa el ID correcto.

                                    return (
                                        <>
                                            <div className="h-10 w-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold border-2 border-white shadow-sm overflow-hidden">
                                                {currentUserInList?.photoURL ? (
                                                    <img src={currentUserInList.photoURL} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span>{displayName[0]}</span>
                                                )}
                                            </div>
                                            <div>
                                                <span className="block text-xs font-bold text-blue-600 uppercase">Venta asignada a:</span>
                                                <span className="font-bold text-gray-900">
                                                    {currentUserInList?.displayName || currentUserInList?.email || 'Mí mismo (Confirmado)'}
                                                </span>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        )}
                    </div>

                    {/* Datos Alumno */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre Alumno</label>
                            <input
                                name="studentName"
                                value={formData.studentName}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                placeholder="Ej. Juan Pérez"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Celular</label>
                            <input
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                placeholder="Ej. 999 888 777"
                            />
                        </div>
                    </div>



                    {/* Email y Curso */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Correo Electrónico</label>
                            <input
                                type="email"
                                name="studentEmail"
                                value={formData.studentEmail}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                placeholder="ejemplo@correo.com"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Curso</label>
                            <input
                                name="course"
                                value={formData.course}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                placeholder="Ej. Excel Avanzado"
                            />
                        </div>
                    </div>

                    {/* Fecha y Valores Económicos */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Fecha Venta</label>
                            <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                            />
                        </div>
                    </div>

                    <div className="h-px bg-gray-200 my-2"></div>

                    {/* Valores Económicos */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1 text-blue-600">Precio Total (S/)</label>
                            <input
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold bg-white"
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">A Cuenta / 1er Pago</label>
                            <input
                                type="number"
                                name="firstPayment"
                                value={formData.firstPayment}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    {/* Cálculos Automáticos Visuales */}
                    <div className="bg-blue-50 rounded-xl p-4 grid grid-cols-3 gap-2 text-center">
                        <div>
                            <span className="block text-xs text-blue-400 font-bold mb-1">DEUDA</span>
                            <span className={`text - lg font - bold ${due > 0 ? 'text-red-500' : 'text-green-500'} `}>
                                S/ {due.toFixed(2)}
                            </span>
                        </div>
                        <div>
                            <span className="block text-xs text-blue-400 font-bold mb-1">NETO (Sin IGV)</span>
                            <span className="text-lg font-bold text-gray-700">S/ {net.toFixed(2)}</span>
                        </div>
                        <div className="relative">
                            <span className="block text-xs text-purple-600 font-bold mb-1">TU COMISIÓN</span>
                            <span className="text-xl font-extrabold text-purple-600">S/ {commission.toFixed(2)}</span>
                            <span className="absolute -top-2 -right-2 bg-purple-200 text-purple-800 text-[10px] px-1 rounded-full">
                                {commissionRate * 100}%
                            </span>
                            <span className="block text-[9px] text-gray-400 mt-1">Base -30% Imp. Renta: S/ {base.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Evidencia */}
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Evidencia (Foto Chat/Voucher)</label>
                        <div className={`border - 2 border - dashed rounded - xl p - 4 text - center cursor - pointer hover: bg - gray - 50 transition - colors ${formData.evidenceFile ? 'border-blue-400' : 'border-gray-300'} `}>
                            <input type="file" onChange={handleFileChange} accept="image/*" className="hidden" id="evidence-upload" />
                            <label htmlFor="evidence-upload" className="cursor-pointer block w-full h-full">
                                {previewUrl ? (
                                    <div className="relative h-32 mx-auto">
                                        <img src={previewUrl} className="h-full mx-auto object-contain rounded-lg" alt="Preview" />
                                        <span className="absolute bottom-1 right-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">Cambiar</span>
                                    </div>
                                ) : (
                                    <div className="py-4">
                                        <FiImage className="mx-auto text-gray-400 mb-2" size={30} />
                                        <span className="text-sm text-gray-500">Haz clic para subir imagen</span>
                                    </div>
                                )}
                            </label>
                        </div>
                    </div>

                    <div className="pt-4 flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors">Cancelar</button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-lg shadow-blue-200 transition-all transform hover:scale-[1.02] flex justify-center items-center gap-2"
                        >
                            {isSaving ? <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span> : <><FiPlus /> Registrar Venta</>}
                        </button>
                    </div>

                </form>
            </div >
        </div >
    );
};

// --- SUPER VENDEDOR IA (pestaña de simulación + entrenamiento) ---
const SuperVendedorPanel = () => {
    const [knowledge, setKnowledge] = useState([]);
    const [loadingK, setLoadingK] = useState(true);
    const [mode, setMode] = useState('sell'); // 'sell' = la IA vende | 'customer' = la IA es cliente
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    const loadKnowledge = async () => {
        setLoadingK(true);
        try {
            setKnowledge(await getSalesKnowledge());
        } catch (e) {
            console.warn('Error cargando conocimiento', e);
        } finally {
            setLoadingK(false);
        }
    };
    useEffect(() => { loadKnowledge(); }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    // Texto de conocimiento (prioriza ventas válidas, limita para no saturar)
    const buildKnowledgeText = () => {
        const sorted = [...knowledge].sort((a, b) => (b.isValid === a.isValid) ? 0 : (b.isValid ? 1 : -1));
        return sorted.slice(0, 60).map((k, i) => `${i + 1}. (${k.course || 'curso'}) ${k.insight}`).join('\n');
    };

    const switchMode = (m) => {
        if (m === mode) return;
        setMode(m);
        setMessages([]);
        setInput('');
    };

    const send = async () => {
        const text = input.trim();
        if (!text || sending) return;
        const newMsgs = [...messages, { role: 'user', text }];
        setMessages(newMsgs);
        setInput('');
        setSending(true);
        try {
            const reply = await superVendedorChat(newMsgs, { mode, knowledgeText: buildKnowledgeText() });
            setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
        } catch (e) {
            setMessages(prev => [...prev, { role: 'assistant', text: '⚠️ No pude responder. Intenta de nuevo.' }]);
        } finally {
            setSending(false);
        }
    };

    const validCount = knowledge.filter(k => k.isValid !== false).length;

    return (
        <div className="max-w-3xl mx-auto">
            {/* Encabezado / stats */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-5 text-white shadow-lg mb-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h2 className="text-xl font-extrabold flex items-center gap-2"><FiTrendingUp /> Super Vendedor IA</h2>
                        <p className="text-indigo-100 text-sm mt-1">Aprende de cada venta analizada y simula la conversación para WhatsApp.</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold">{loadingK ? '…' : knowledge.length}</div>
                            <div className="text-[11px] text-indigo-200 uppercase tracking-wide">Conversaciones aprendidas</div>
                        </div>
                        <button onClick={loadKnowledge} className="p-2 bg-white/15 hover:bg-white/25 rounded-lg transition-colors" title="Recargar conocimiento">
                            <FiRefreshCw className={loadingK ? 'animate-spin' : ''} />
                        </button>
                    </div>
                </div>
                {knowledge.length === 0 && !loadingK && (
                    <div className="mt-3 text-xs bg-white/10 rounded-lg p-2.5">
                        Aún no hay conocimiento. Ve a <b>Ventas → Analizar todas (IA)</b> para que aprenda de tus conversaciones reales.
                    </div>
                )}
            </div>

            {/* Selector de modo */}
            <div className="flex gap-2 mb-3 bg-gray-100 p-1 rounded-xl w-full sm:w-fit">
                <button
                    onClick={() => switchMode('sell')}
                    className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2 ${mode === 'sell' ? 'bg-white text-indigo-700 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    🤖 La IA me vende
                </button>
                <button
                    onClick={() => switchMode('customer')}
                    className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2 ${mode === 'customer' ? 'bg-white text-indigo-700 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    🧑 Yo vendo (IA cliente)
                </button>
            </div>

            {/* Chat estilo WhatsApp */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" style={{ height: '60vh' }}>
                <div className="px-4 py-3 bg-[#075E54] text-white flex items-center gap-3 shrink-0">
                    <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                        {mode === 'sell' ? <FiTrendingUp /> : <FiUser />}
                    </div>
                    <div>
                        <div className="font-bold text-sm">{mode === 'sell' ? 'Super Vendedor Hingenia' : 'Cliente potencial'}</div>
                        <div className="text-[11px] text-green-100">{sending ? 'escribiendo…' : 'en línea'}</div>
                    </div>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" style={{ background: '#ECE5DD' }}>
                    {messages.length === 0 && (
                        <div className="text-center text-gray-500 text-sm mt-8 px-6">
                            {mode === 'sell'
                                ? 'Escribe como un cliente interesado. Ej: "Hola, vi el curso de AutoCAD ¿cuánto cuesta?"'
                                : 'Tú eres el vendedor. Saluda al cliente y empieza a venderle. Ej: "Hola 👋 ¿te interesa nuestro curso de ETABS?"'}
                        </div>
                    )}
                    {messages.map((m, idx) => (
                        <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm shadow-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#DCF8C6] text-gray-800 rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none'}`}>
                                {m.text}
                            </div>
                        </div>
                    ))}
                    {sending && (
                        <div className="flex justify-start">
                            <div className="bg-white px-3 py-2 rounded-lg rounded-bl-none shadow-sm text-gray-400 text-sm flex items-center gap-2">
                                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></span> escribiendo…
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-end gap-2 shrink-0">
                    <textarea
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder="Escribe un mensaje…"
                        rows={1}
                        className="flex-1 resize-none px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:ring-2 focus:ring-[#075E54]/40 outline-none max-h-24"
                    />
                    <button
                        onClick={send}
                        disabled={sending || !input.trim()}
                        className="p-3 bg-[#075E54] text-white rounded-full hover:bg-[#054c43] transition-colors disabled:opacity-50 shrink-0"
                        title="Enviar"
                    >
                        <FiSend size={16} />
                    </button>
                </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-2 text-center">
                Aprende de {validCount} venta(s) válida(s) de {knowledge.length} analizada(s). Mientras más ventas analices, mejor venderá.
            </p>
        </div>
    );
};

// Página Principal
const SalesAdvisorPage = () => {
    const { user } = useAuth();
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [zoomedImage, setZoomedImage] = useState(null);

    // AI Analysis State
    const [analyzingSaleId, setAnalyzingSaleId] = useState(null);
    const [aiResult, setAiResult] = useState(null);
    const [showAiModal, setShowAiModal] = useState(false);

    // Batch AI Analysis State
    const [batchAnalyzing, setBatchAnalyzing] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, current: '', ok: 0, fail: 0 });

    // Lecciones aprendidas (memoria global de la IA de ventas)
    const [aiLessons, setAiLessons] = useState([]);

    // Chat de revisión/enseñanza dentro del modal de Análisis IA
    const [chatSale, setChatSale] = useState(null);        // venta en discusión (con id, evidenceUrl, etc.)
    const [chatMessages, setChatMessages] = useState([]);  // [{ role, text }]
    const [chatInput, setChatInput] = useState('');
    const [chatSending, setChatSending] = useState(false);

    // Nuevos estados para filtros y admin
    const isAdmin = user?.email === 'vcecursos@gmail.com' || user?.role === 'admin';

    // CAMBIO INTEGRAL: Directorio Universal
    const [allUsersList, setAllUsersList] = useState([]); // Lista plana para selectores
    const [usersMap, setUsersMap] = useState({}); // Diccionario ID->User para búsquedas rápidas
    const [selectedSeller, setSelectedSeller] = useState('all');

    // Dynamic Rules & Memory
    const [rules, setRules] = useState([]);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [showMemoryModal, setShowMemoryModal] = useState(false);

    // Pestañas: ventas | supervendedor
    const [activeTab, setActiveTab] = useState('ventas');

    // Cargar Reglas (Con Seeding opcional)
    useEffect(() => {
        const fetchRules = async () => {
            // Intentar seed por si es la primera vez
            await seedCommissionRules(COMMISSION_RULES);
            const data = await getCommissionRules();
            setRules(data);
        };
        fetchRules();
    }, []);

    // Cargar lecciones aprendidas (memoria global de la IA)
    const refreshLessons = async () => {
        try {
            const data = await getSalesLessons();
            setAiLessons(data);
        } catch (e) {
            console.warn('No se pudieron cargar las lecciones IA', e);
        }
    };
    useEffect(() => { refreshLessons(); }, []);

    // Texto de reglas y lecciones para alimentar a la IA
    const buildRulesText = () => rules.map(r => `${r.title}: ${r.situation} -> ${r.result} (${r.reason})`).join('\n');
    const buildLessonsText = () => aiLessons.map((l, i) => `${i + 1}. ${l.lesson}`).join('\n');

    // Cargar Directorio COMPLETO de Usuarios
    useEffect(() => {
        const fetchDirectory = async () => {
            try {
                // 1. Cargar TODOS los usuarios de la colección 'users'
                // No usamos getApprovedUsers porque filtra demasiado. Queremos todo para el historial.
                const q = query(collection(db, 'users'));
                const snapshot = await getDocs(q);

                const fullDirectory = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // 2. Crear Mapa de Búsqueda Rápida (Diccionario)
                const map = {};
                fullDirectory.forEach(u => {
                    map[u.id] = u;
                });

                setAllUsersList(fullDirectory);
                setUsersMap(map);

            } catch (err) {
                console.error("Error loading user directory", err);
                toast.error("Error cargando directorio de usuarios");
            }
        };

        fetchDirectory();

        if (!isAdmin && user?.uid) {
            setSelectedSeller(user.uid);
        }
    }, [isAdmin, user]);

    useEffect(() => {
        if (user?.uid) {
            // Si es admin espera a que selectedSeller esté definido (o default 'all')
            // Si no es admin, ya se seteó en el efecto anterior
            loadSales();
        }
    }, [user, currentMonth, selectedSeller]);

    const loadSales = async () => {
        setLoading(true);
        try {
            // Determinar ID para filtrar:
            // Si es Admin y selecciona 'all' -> null (trae todas)
            // Si es Admin y selecciona ID -> ID
            // Si no es Admin -> user.uid
            let filterId = null;
            if (isAdmin) {
                filterId = selectedSeller === 'all' ? null : selectedSeller;
            } else {
                filterId = user.uid;
            }

            const data = await salesService.getSales(filterId, currentMonth);
            setSales(data);
        } catch (error) {
            toast.error('Error al cargar ventas');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSale = async (formData) => {
        setIsSaving(true);
        try {
            await salesService.createSale({
                advisorId: formData.advisorId || user.uid, // Usar el seleccionado o el actual
                ...formData
            }, formData.evidenceFile);

            toast.success(`¡Venta registrada! Comisión: S / ${(formData.netAmount || 0) * (formData.price > 100 ? 0.15 : 0.10)} `);
            setIsModalOpen(false);
            loadSales(); // Recargar lista
        } catch (error) {
            toast.error('Error al registrar venta');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id, evidenceUrl) => {
        if (!window.confirm('¿Seguro que quieres eliminar este registro?')) return;
        try {
            await salesService.deleteSale(id, evidenceUrl);
            toast.success('Registro eliminado');
            loadSales();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const handleToggleStatus = async (sale) => {
        if (!isAdmin) return;
        const newStatus = !sale.isValid;
        try {
            await salesService.updateSaleStatus(sale.id, newStatus);
            toast.success(`Venta marcada como ${newStatus ? 'VÁLIDA' : 'INVÁLIDA'} `);
            // Actualizar localmente
            setSales(sales.map(s => s.id === sale.id ? { ...s, isValid: newStatus } : s));
        } catch (error) {
            toast.error("No se pudo actualizar el estado");
        }
    };

    // Marcar/desmarcar que una venta fue revisada manualmente
    const handleToggleManualReview = async (sale) => {
        const newVal = !sale.manualReviewed;
        // Optimista
        setSales(prev => prev.map(s => s.id === sale.id ? { ...s, manualReviewed: newVal } : s));
        try {
            await updateDoc(doc(db, 'sales_records', sale.id), { manualReviewed: newVal });
        } catch (e) {
            console.error('Error marcando revisión manual', e);
            toast.error('No se pudo guardar la revisión');
            setSales(prev => prev.map(s => s.id === sale.id ? { ...s, manualReviewed: !newVal } : s));
        }
    };

    // --- AI ANALYSIS HANDLER ---
    const handleAnalyzeSale = async (sale) => {
        if (!sale.evidenceUrl) {
            alert("No hay evidencia para analizar.");
            return;
        }

        setAnalyzingSaleId(sale.id);
        try {
            const result = await analyzeSaleEvidence(sale.evidenceUrl, buildRulesText(), buildLessonsText());
            setAiResult({ ...result, saleId: sale.id });
            // Entrenar al Super Vendedor con el aprendizaje de esta venta
            if (result.salesInsight) {
                saveSalesKnowledge(sale.id, {
                    insight: result.salesInsight,
                    studentName: sale.studentName,
                    course: sale.course,
                    price: parseFloat(sale.price) || 0,
                    date: sale.date,
                    advisorId: sale.advisorId,
                    isValid: result.isValid
                }).catch(e => console.warn('No se pudo guardar conocimiento', e));
            }
            // Preparar contexto del chat de revisión
            setChatSale(sale);
            setChatMessages([]);
            setChatInput('');
            setShowAiModal(true);
        } catch (error) {
            console.error("AI Error:", error);
            alert("Error al analizar con IA: " + error.message);
        } finally {
            setAnalyzingSaleId(null);
        }
    };

    // --- BATCH AI ANALYSIS (todas las ventas con evidencia) ---
    const handleAnalyzeAll = async () => {
        const targets = filteredSales.filter(s => s.evidenceUrl);
        if (targets.length === 0) {
            toast.error('No hay ventas con evidencia para analizar.');
            return;
        }
        if (!window.confirm(`Analizar ${targets.length} venta(s) con IA. El veredicto se aplicará automáticamente. ¿Continuar?`)) return;

        setBatchAnalyzing(true);
        setBatchProgress({ done: 0, total: targets.length, current: '', ok: 0, fail: 0 });

        const rulesText = buildRulesText();
        const lessonsText = buildLessonsText();
        let ok = 0, fail = 0;

        for (let i = 0; i < targets.length; i++) {
            const sale = targets[i];
            setBatchProgress({ done: i, total: targets.length, current: sale.studentName, ok, fail });
            try {
                const result = await analyzeSaleEvidence(sale.evidenceUrl, rulesText, lessonsText);
                await updateDoc(doc(db, 'sales_records', sale.id), {
                    isValid: result.isValid,
                    aiAnalyzed: true,
                    aiReason: result.reason
                });
                setSales(prev => prev.map(s =>
                    s.id === sale.id
                        ? { ...s, isValid: result.isValid, aiAnalyzed: true, aiReason: result.reason }
                        : s
                ));
                // Entrenar al Super Vendedor con cada venta
                if (result.salesInsight) {
                    saveSalesKnowledge(sale.id, {
                        insight: result.salesInsight,
                        studentName: sale.studentName,
                        course: sale.course,
                        price: parseFloat(sale.price) || 0,
                        date: sale.date,
                        advisorId: sale.advisorId,
                        isValid: result.isValid
                    }).catch(e => console.warn('No se pudo guardar conocimiento', e));
                }
                ok++;
            } catch (err) {
                console.error('Batch AI error en venta', sale.id, err);
                fail++;
            }
            setBatchProgress({ done: i + 1, total: targets.length, current: sale.studentName, ok, fail });
        }

        setBatchAnalyzing(false);
        toast.success(`Análisis IA completo: ${ok} analizadas${fail ? `, ${fail} con error` : ''}.`);
        if (ok > 0) {
            confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        }
    };

    const applyAiVerdict = async () => {
        if (!aiResult) return;

        // Mapear 'active'/'rejected' a los valores correctos
        // NOTA: El sistema usa boolean 'isValid' para visualización, pero 'status' en DB?
        // Revisando handleToggleStatus: Actualiza 'isValid'. 
        // Si result.suggestedStatus es 'active', isValid = true.

        try {
            const newIsValid = aiResult.isValid;
            const saleRef = doc(db, 'sales_records', aiResult.saleId);
            await updateDoc(saleRef, {
                isValid: newIsValid,
                aiAnalyzed: true,
                aiReason: aiResult.reason
            });

            // Actualizar estado local
            setSales(prev => prev.map(s =>
                s.id === aiResult.saleId
                    ? { ...s, isValid: newIsValid, aiAnalyzed: true, aiReason: aiResult.reason }
                    : s
            ));

            setShowAiModal(false);
            setAiResult(null);

            if (newIsValid) {
                confetti({
                    particleCount: 80,
                    spread: 60,
                    origin: { y: 0.6 },
                    colors: ['#10B981', '#34D399']
                });
            }

        } catch (error) {
            console.error("Error aplicando veredicto:", error);
            alert("Error al actualizar la venta.");
        }
    };

    const handleViewVerification = (sale) => {
        setAiResult({
            isValid: sale.isValid,
            reason: sale.aiReason || "No se registró el motivo detallado.",
            ruleApplied: "Historial",
            confidence: "high",
            readOnly: true,
            saleId: sale.id
        });
        setChatSale(sale);
        setChatMessages([]);
        setChatInput('');
        setShowAiModal(true);
    };

    // Aplica un veredicto (valid/invalid) a una venta concreta desde el chat
    const applyVerdictToSale = async (saleId, isValid, reason) => {
        try {
            await updateDoc(doc(db, 'sales_records', saleId), {
                isValid,
                aiAnalyzed: true,
                ...(reason ? { aiReason: reason } : {})
            });
            setSales(prev => prev.map(s =>
                s.id === saleId ? { ...s, isValid, aiAnalyzed: true, ...(reason ? { aiReason: reason } : {}) } : s
            ));
            setAiResult(prev => prev ? { ...prev, isValid, ...(reason ? { reason } : {}) } : prev);
            setChatSale(prev => prev ? { ...prev, isValid } : prev);
        } catch (e) {
            console.error('Error aplicando veredicto desde chat', e);
        }
    };

    // Enviar mensaje en el chat de revisión/enseñanza
    const handleSendChat = async () => {
        const text = chatInput.trim();
        if (!text || chatSending || !chatSale) return;

        const newMessages = [...chatMessages, { role: 'user', text }];
        setChatMessages(newMessages);
        setChatInput('');
        setChatSending(true);

        try {
            const ctx = {
                rulesText: buildRulesText(),
                lessonsText: buildLessonsText(),
                currentVerdict: chatSale.isValid !== false,
                saleInfo: `Alumno: ${chatSale.studentName} | Curso: ${chatSale.course} | Precio: S/ ${chatSale.price} | Fecha: ${chatSale.date}`
            };
            const res = await chatSaleAnalysis(chatSale.evidenceUrl, newMessages, ctx);

            setChatMessages(prev => [...prev, { role: 'assistant', text: res.reply }]);

            // Aplicar veredicto a esta venta si la IA lo dedujo de tu corrección
            if (res.suggestedVerdict) {
                const isValid = res.suggestedVerdict === 'valid';
                await applyVerdictToSale(chatSale.id, isValid, res.reply);
                toast.success(`Venta marcada como ${isValid ? 'VÁLIDA' : 'INVÁLIDA'}`);
            }

            // Guardar lección general en la memoria
            if (res.lessonToSave) {
                await addSalesLesson({
                    lesson: res.lessonToSave,
                    verdict: res.suggestedVerdict || 'note',
                    context: ctx.saleInfo,
                    createdByEmail: user?.email || ''
                });
                await refreshLessons();
                setChatMessages(prev => [...prev, { role: 'system', text: `📚 Lección guardada: "${res.lessonToSave}"` }]);
                toast.success('Lección guardada en la memoria de la IA');
            }
        } catch (e) {
            console.error('Error en chat IA', e);
            setChatMessages(prev => [...prev, { role: 'system', text: '⚠️ No se pudo procesar el mensaje. Intenta de nuevo.' }]);
        } finally {
            setChatSending(false);
        }
    };

    // Filtrado simple (Nombre o Curso)
    const filteredSales = useMemo(() => {
        return sales.filter(s =>
            s.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.course.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [sales, searchTerm]);

    // KPIs - Solo ventas VÁLIDAS
    const validSales = sales.filter(s => s.isValid !== false); // Default true si no existe
    const totalCommission = useMemo(() => validSales.reduce((acc, curr) => acc + computeCommission(curr.price).commission, 0), [validSales]);
    const totalSalesCount = validSales.length;

    return (
        <div className={`h-full overflow-y-auto bg-gray-50/50 p-6 md:p-10 font-sans transition-all duration-300 ${zoomedImage ? 'md:pr-[500px]' : ''}`}>

            {/* Pestañas */}
            <div className="flex gap-1 mb-6 border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('ventas')}
                    className={`px-4 py-2.5 text-sm font-bold transition-colors border-b-2 -mb-px ${activeTab === 'ventas' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                    💰 Ventas
                </button>
                {isAdmin && (
                    <button
                        onClick={() => setActiveTab('supervendedor')}
                        className={`px-4 py-2.5 text-sm font-bold transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${activeTab === 'supervendedor' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    >
                        <FiTrendingUp size={15} /> Super Vendedor IA
                    </button>
                )}
            </div>

            {activeTab === 'supervendedor' ? <SuperVendedorPanel /> : (<>

            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-6">
                    <div className="min-w-max">
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">Panel de Ventas 💰</h1>
                        <p className="text-gray-500">Administra tus registros y comisiones</p>

                        {/* Admin Tools */}
                        {isAdmin && (
                            <div className="flex gap-2 mt-3">
                                <button
                                    onClick={() => setShowRulesModal(true)}
                                    className="px-3 py-1.5 bg-gray-800 text-white text-xs font-bold rounded-lg flex items-center gap-2 hover:bg-gray-700 transition-colors"
                                >
                                    <FiAlertCircle /> Gestionar Reglas
                                </button>
                                <button
                                    onClick={() => setShowMemoryModal(true)}
                                    className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors"
                                >
                                    <FiCpu /> Memoria IA
                                </button>
                                <button
                                    onClick={handleAnalyzeAll}
                                    disabled={batchAnalyzing}
                                    className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg flex items-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-60"
                                >
                                    <FiCpu className={batchAnalyzing ? 'animate-spin' : ''} /> Analizar todas (IA)
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Commission Rules Widget (Desktop) */}
                    <div className="hidden md:block w-full">
                        <CommissionRulesWidget rules={rules} />
                    </div>

                    {/* KPI Card */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-4 shadow-lg text-white w-full md:w-auto min-w-[240px] transform hover:scale-105 transition-transform duration-300 xl:ml-0 ml-auto">
                        <div className="text-blue-100 text-sm font-medium mb-1">TUS COMISIONES (MES)</div>
                        <div className="text-3xl font-bold flex items-center gap-2">
                            S/ {totalCommission.toFixed(2)}
                            {totalCommission > 0 && <span className="text-xs bg-white/20 px-2 py-1 rounded-full">🚀</span>}
                        </div>
                        <div className="text-xs text-blue-200 mt-2 flex justify-between">
                            <span>{totalSalesCount} Ventas válidas</span>
                            <span>{currentMonth}</span>
                        </div>
                    </div>
                </div>

                {/* Commission Rules Widget (Mobile - shown below) */}
                <div className="md:hidden mt-4">
                    <CommissionRulesWidget />
                </div>


                {/* Seller Profile Header (Para Admin con selección o Usuario Normal) */}
                {((isAdmin && selectedSeller !== 'all') || (!isAdmin && user)) && (
                    <div className="mt-6 bg-white rounded-xl p-4 border border-blue-100 shadow-sm flex items-center gap-4 animate-fadeIn">
                        {(() => {
                            // Si es admin, busca el seleccionado. Si no, usa el user actual (pero búscalo en sellers para tener foto completa si está disponible)
                            const targetId = isAdmin ? selectedSeller : user?.uid;
                            // Intentamos buscar en el mapa primero
                            const seller = usersMap[targetId] || (targetId === user?.uid ? user : null);

                            if (!seller) return null;

                            return (
                                <>
                                    <div className="h-16 w-16 rounded-full border-2 border-blue-500 p-0.5">
                                        <img
                                            src={seller.photoURL || `https://ui-avatars.com/api/?name=${seller.displayName || seller.email}&background=0D8ABC&color=fff`}
                                            alt={seller.displayName}
                                            className="w-full h-full rounded-full object-cover"
                                        />
                                    </div >
                                    <div>
                                        <div className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-1">
                                            {isAdmin ? 'Vendedor Seleccionado' : 'Mi Perfil de Ventas'}
                                        </div>
                                        <h2 className="text-xl font-bold text-gray-900">{seller.displayName || seller.email}</h2>
                                        <p className="text-gray-500 text-sm flex items-center gap-2">
                                            {seller.email}
                                            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-bold">ACTIVO</span>
                                        </p>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-6 flex flex-col items-center gap-4">

                {/* Fila superior de controles */}
                <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-center">
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <div className="relative w-full md:w-64">
                            <FiSearch className="absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar alumno o curso..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-gray-900 bg-white placeholder-gray-500"
                            />
                        </div>
                        <input
                            type="month"
                            value={currentMonth}
                            onChange={e => setCurrentMonth(e.target.value)}
                            className="border-gray-200 bg-gray-50 rounded-xl px-3 py-2 text-gray-700 bg-white"
                        />
                    </div>

                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="w-full md:w-auto flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg shadow-gray-200 transition-all hover:-translate-y-1"
                    >
                        <FiPlus size={18} />
                        Nueva Venta
                    </button>
                </div>

                {/* Filtro de Vendedor (Solo Admin) */}
                {
                    isAdmin && (
                        <div className="w-full border-t pt-4 flex flex-col md:flex-row items-center gap-3">
                            <div className="flex items-center gap-2 text-gray-500">
                                <FiFilter aria-hidden="true" />
                                <span className="text-sm font-bold text-gray-600">Filtrar Vendedor:</span>
                            </div>
                            <div className="flex-1 w-full md:w-auto">
                                <SellerSelect
                                    sellers={allUsersList}
                                    selectedSeller={selectedSeller}
                                    onChange={setSelectedSeller}
                                    currentUserId={user.uid}
                                />
                            </div>
                        </div>
                    )
                }
            </div>

            {/* Table */}
            {
                loading ? (
                    <div className="p-12 text-center text-gray-400">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        Cargando registros...
                    </div>
                ) : filteredSales.length === 0 ? (
                    <div className="p-12 text-center text-gray-400 flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-300">
                            <FiDollarSign size={32} />
                        </div>
                        <p className="text-lg font-medium text-gray-600">No hay ventas registradas</p>
                    </div>
                ) : (
                    <>
                        {/* Mobile Cards View */}
                        <div className="md:hidden grid grid-cols-1 gap-4 pb-20">
                            {filteredSales.map((sale) => {
                                const isValid = sale.isValid !== false;
                                return (
                                    <div key={sale.id} className={`bg-white p-4 rounded-xl shadow-sm border border-${isValid ? 'gray-100' : 'red-100'} relative`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h3 className={`font-bold text-gray-900 ${!isValid ? 'line-through text-gray-400' : ''}`}>{sale.studentName}</h3>
                                                <p className="text-xs text-gray-500">{sale.course}</p>
                                                <div className="text-[10px] text-gray-400 mt-1">{sale.date}</div>
                                            </div>
                                            {/* Status Badge */}
                                            <span className={`px-2 py-1 rounded text-[10px] font-bold ${isValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {isValid ? 'APROBADO' : 'INVÁLIDO'}
                                            </span>
                                        </div>

                                        {/* Financials */}
                                        <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-3">
                                            <div>
                                                <span className="text-[10px] text-gray-400 uppercase tracking-wider block">Comisión</span>
                                                <span className={`font-bold ${isValid ? 'text-purple-600' : 'text-gray-400'}`}>
                                                    S/ {isValid ? computeCommission(sale.price).commission.toFixed(2) : '0.00'}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-[10px] text-gray-400 uppercase tracking-wider block">Precio</span>
                                                <span className="font-medium text-gray-800">S/ {parseFloat(sale.price).toFixed(2)}</span>
                                            </div>
                                        </div>

                                        {/* Footer Actions */}
                                        <div className="flex justify-between items-center border-t border-gray-100 pt-3">
                                            <div className="flex items-center gap-3">
                                                {sale.evidenceUrl && (
                                                    <button onClick={() => setZoomedImage(sale.evidenceUrl)} className="text-blue-500 hover:bg-blue-50 p-2 rounded-full">
                                                        <FiImage size={18} />
                                                    </button>
                                                )}
                                                <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-gray-500" title="Revisado manualmente">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!sale.manualReviewed}
                                                        onChange={() => handleToggleManualReview(sale)}
                                                        className="w-4 h-4 rounded accent-green-600"
                                                    />
                                                    Revisado
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {isAdmin && sale.evidenceUrl && (
                                                    analyzingSaleId === sale.id ? (
                                                        <div className="p-2 animate-pulse"><FiCpu className="animate-spin text-indigo-600" /></div>
                                                    ) : (
                                                        <button onClick={() => handleAnalyzeSale(sale)} className={`p-2 rounded-full ${sale.aiAnalyzed ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400'}`}>
                                                            <FiCpu size={18} />
                                                        </button>
                                                    )
                                                )}
                                                {sale.aiAnalyzed && (
                                                    <button onClick={() => handleViewVerification(sale)} className="text-blue-500 hover:bg-blue-50 p-2 rounded-full">
                                                        <FiInfo size={18} />
                                                    </button>
                                                )}
                                                <button onClick={() => handleDelete(sale.id, sale.evidenceUrl)} className="text-red-400 hover:bg-red-50 p-2 rounded-full">
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden md:flex bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 flex-1 flex-col min-h-0">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Vendedor</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Alumno</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Curso</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Estado</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Precio</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Neto</th>
                                            <th className="px-6 py-4 text-right text-xs font-bold text-purple-600 uppercase tracking-wider">Comisión</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Evidencia</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Revisado</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {filteredSales.map((sale) => {
                                            const isValid = sale.isValid !== false; // Default true
                                            return (
                                                <tr key={sale.id} className={`hover:bg-blue-50/30 transition-colors group ${!isValid ? 'bg-red-50' : ''}`}>
                                                    <td className="p-4 font-medium text-gray-600 space-nowrap text-gray-900">{sale.date}</td>
                                                    <td className="p-4">
                                                        {(() => {
                                                            // Búsqueda robusta del vendedor
                                                            // 1. Buscar en la lista de sellers probada
                                                            // 1. Búsqueda DIRECTA en el mapa (O(1))
                                                            let s = usersMap[sale.advisorId];

                                                            // 2. Fallback al usuario actual si no está en el mapa
                                                            if (!s && sale.advisorId === user?.uid) {
                                                                s = user;
                                                            }

                                                            return (
                                                                <div className="flex items-center gap-2" title={s?.displayName || 'Desconocido'}>
                                                                    <div className="w-6 h-6 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 border border-gray-300">
                                                                        {s?.photoURL ? (
                                                                            <img src={s.photoURL} alt="" className="w-full h-full object-cover" />
                                                                        ) : (
                                                                            <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-gray-500">
                                                                                {s?.displayName?.[0] || '?'}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <span className="text-xs font-medium text-gray-700 truncate max-w-[100px] hidden xl:block">
                                                                        {s?.displayName?.split(' ')[0] || s?.email?.split('@')[0] || '...'}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="p-4">
                                                        <div className={`font-bold text-gray-900 ${!isValid ? 'line-through text-gray-400' : ''}`}>{sale.studentName}</div>
                                                        <div className="text-xs text-gray-400">{sale.phoneNumber}</div>
                                                    </td>
                                                    <td className="p-4">
                                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${isValid ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}>
                                                            {sale.course}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        {isAdmin ? (
                                                            <button
                                                                onClick={() => handleToggleStatus(sale)}
                                                                className={`flex items-center justify-center gap-1 mx-auto px-3 py-1 rounded-full text-xs font-bold transition-all ${isValid ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                                                title="Clic para cambiar estado"
                                                            >
                                                                {isValid ? <><FiCheckCircle /> APROBADO</> : <><FiXCircle /> INVÁLIDO</>}
                                                            </button>
                                                        ) : (
                                                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${isValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                {isValid ? <><FiCheckCircle /> APROBADO</> : <><FiXCircle /> INVÁLIDO</>}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className={`p-4 text-center font-medium ${!isValid ? 'text-gray-400 decoration-slice' : 'text-gray-900'}`}>
                                                        S/ {parseFloat(sale.price).toFixed(2)}
                                                    </td>
                                                    {(() => {
                                                        const { net, rate, commission } = computeCommission(sale.price);
                                                        return (
                                                            <>
                                                                <td className="p-4 text-center text-gray-500">S/ {net.toFixed(2)}</td>
                                                                <td className={`p-4 text-center font-bold ${isValid ? 'text-purple-600 bg-purple-50/20' : 'text-gray-300'}`}>
                                                                    S/ {isValid ? commission.toFixed(2) : '0.00'}
                                                                    {isValid && <div className="text-[10px] text-purple-400 font-normal">{(rate * 100)}% · -30% I.R.</div>}
                                                                </td>
                                                            </>
                                                        );
                                                    })()}
                                                    <td className="p-4 text-center">
                                                        {sale.evidenceUrl ? (
                                                            <button
                                                                onClick={() => setZoomedImage(sale.evidenceUrl)}
                                                                className="text-gray-400 hover:text-blue-500 transition-colors p-2 rounded-full hover:bg-gray-100 tooltip"
                                                                title="Ver Evidencia"
                                                            >
                                                                <FiImage size={20} />
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-300">-</span>
                                                        )}
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <label className="inline-flex items-center justify-center cursor-pointer" title="Marcar como revisado manualmente">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!sale.manualReviewed}
                                                                onChange={() => handleToggleManualReview(sale)}
                                                                className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer accent-green-600"
                                                            />
                                                        </label>
                                                    </td>
                                                    <td className="p-4 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            {/* AI Analysis Button (Admin Only) */}
                                                            {isAdmin && sale.evidenceUrl && (
                                                                analyzingSaleId === sale.id ? (
                                                                    <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center animate-pulse">
                                                                        <FiCpu className="text-indigo-600 animate-spin" />
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => handleAnalyzeSale(sale)}
                                                                        className={`p-2 rounded-full transition-colors tooltip ${sale.aiAnalyzed ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-300 hover:text-indigo-600 hover:bg-indigo-50'}`}
                                                                        title="Analizar con IA"
                                                                    >
                                                                        <FiCpu size={18} />
                                                                    </button>
                                                                )
                                                            )}

                                                            {sale.aiAnalyzed && (
                                                                <button
                                                                    onClick={() => handleViewVerification(sale)}
                                                                    className="text-blue-500 hover:bg-blue-50 p-2 rounded-full transition-colors"
                                                                    title="Ver motivo de IA"
                                                                >
                                                                    <FiInfo size={18} />
                                                                </button>
                                                            )}

                                                            <button
                                                                onClick={() => handleDelete(sale.id, sale.evidenceUrl)}
                                                                className="text-gray-300 hover:text-red-500 transition-colors p-2"
                                                                title="Eliminar registro"
                                                            >
                                                                <FiTrash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )
            }

            {/* Modals */}
            <NewSaleModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleCreateSale}
                isSaving={isSaving}
                isAdmin={isAdmin}
                sellers={allUsersList} // Pasamos la lista COMPLETA al modal
                currentUserId={user?.uid}
            />

            {
                zoomedImage && (
                    <ImageZoomModal
                        imageUrl={zoomedImage}
                        onClose={() => setZoomedImage(null)}
                    />
                )
            }

            {/* AI Result Modal */}
            {
                showAiModal && aiResult && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowAiModal(false)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden animate-fade-in-up" onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className={`p-4 text-white flex justify-between items-center shrink-0 ${aiResult.isValid ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
                                <div className="flex items-center gap-2 font-bold text-lg">
                                    <FiCpu className="text-white/90" />
                                    Análisis IA
                                </div>
                                <button onClick={() => setShowAiModal(false)} className="text-white/80 hover:text-white transition-colors">
                                    <FiX size={24} />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-6 overflow-y-auto">
                                <div className="mb-6 text-center">
                                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 shadow-lg ${aiResult.isValid ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                        {aiResult.isValid ? <FiCheckCircle size={32} /> : <FiX size={32} />}
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-800">
                                        {aiResult.isValid ? 'Venta Válida' : 'Venta Inválida'}
                                    </h3>
                                    <p className="text-sm font-medium text-gray-400 mt-1 uppercase tracking-wide">
                                        Confianza: {aiResult.confidence === 'high' ? 'Alta' : aiResult.confidence === 'medium' ? 'Media' : 'Baja'}
                                    </p>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                                    <div className="flex items-start gap-3 mb-2">
                                        <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded uppercase shrink-0 mt-0.5">
                                            {aiResult.ruleApplied || 'Regla General'}
                                        </span>
                                    </div>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        "{aiResult.reason}"
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3">
                                    {aiResult.readOnly ? (
                                        <button
                                            onClick={() => setShowAiModal(false)}
                                            className="w-full px-4 py-2.5 bg-gray-800 text-white font-bold rounded-xl hover:bg-gray-900 transition-colors"
                                        >
                                            Entendido
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => setShowAiModal(false)}
                                                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-600 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                onClick={applyAiVerdict}
                                                className={`flex-1 px-4 py-2.5 text-white font-bold rounded-xl shadow-lg shadow-gray-200 hover:shadow-xl hover:-translate-y-0.5 transition-all ${aiResult.isValid ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
                                            >
                                                Aplicar {aiResult.isValid ? 'Aprobado' : 'Rechazo'}
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* --- Chat de revisión / enseñanza --- */}
                                {chatSale && (
                                    <div className="mt-6 border-t border-gray-100 pt-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <FiMessageSquare className="text-indigo-600" />
                                            <span className="text-sm font-bold text-gray-700">Revisar / Enseñar a la IA</span>
                                        </div>
                                        <p className="text-[11px] text-gray-400 mb-3 leading-snug">
                                            ¿No estás de acuerdo? Explícale el caso (ej. "esta NO vale: el cliente volvió a los 4 días sin que la asesora le escribiera"). La IA corrige esta venta y <b>guarda la lección</b> para las próximas.
                                        </p>

                                        {/* Historial del chat */}
                                        {chatMessages.length > 0 && (
                                            <div className="space-y-2 mb-3 max-h-56 overflow-y-auto pr-1">
                                                {chatMessages.map((m, idx) => (
                                                    m.role === 'system' ? (
                                                        <div key={idx} className="text-[11px] text-center text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
                                                            {m.text}
                                                        </div>
                                                    ) : (
                                                        <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === 'user' ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`}>
                                                                {m.text}
                                                            </div>
                                                        </div>
                                                    )
                                                ))}
                                                {chatSending && (
                                                    <div className="flex justify-start">
                                                        <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                                                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500"></span> Pensando…
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Input */}
                                        <div className="flex items-end gap-2">
                                            <textarea
                                                value={chatInput}
                                                onChange={e => setChatInput(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                                                placeholder="Explícale por qué vale o no…"
                                                rows={1}
                                                className="flex-1 resize-none px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 outline-none max-h-24"
                                            />
                                            <button
                                                onClick={handleSendChat}
                                                disabled={chatSending || !chatInput.trim()}
                                                className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 shrink-0"
                                                title="Enviar"
                                            >
                                                <FiSend size={16} />
                                            </button>
                                        </div>
                                        {aiLessons.length > 0 && (
                                            <p className="text-[10px] text-gray-400 mt-2 text-right">🧠 {aiLessons.length} lección(es) en memoria</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Overlay de progreso de Análisis Masivo IA */}
            {batchAnalyzing && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <div className="flex items-center gap-2 mb-4 font-bold text-indigo-700 text-lg">
                            <FiCpu className="animate-spin" /> Analizando con IA
                        </div>
                        <div className="flex justify-between items-end mb-1">
                            <span className="text-sm text-gray-500">
                                Analizando <span className="font-bold text-gray-800">{batchProgress.done}</span>/{batchProgress.total}
                            </span>
                            <span className="text-xs text-gray-400">
                                {batchProgress.total > 0 ? Math.round((batchProgress.done / batchProgress.total) * 100) : 0}%
                            </span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-300 ease-out"
                                style={{ width: `${batchProgress.total > 0 ? (batchProgress.done / batchProgress.total) * 100 : 0}%` }}
                            ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3 truncate">
                            {batchProgress.current ? <>Procesando: <span className="font-medium text-gray-700">{batchProgress.current}</span></> : 'Preparando…'}
                        </p>
                        <div className="flex gap-3 mt-2 text-[11px]">
                            <span className="text-green-600 font-bold">✓ {batchProgress.ok} ok</span>
                            {batchProgress.fail > 0 && <span className="text-red-500 font-bold">✕ {batchProgress.fail} error</span>}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-3 text-center">No cierres esta ventana hasta terminar.</p>
                    </div>
                </div>
            )}

            {/* Componentes de gestión Admin */}
            <RulesManagerModal
                isOpen={showRulesModal}
                onClose={() => setShowRulesModal(false)}
                onRulesChange={setRules}
            />
            <AiMemoryLogModal
                isOpen={showMemoryModal}
                onClose={() => setShowMemoryModal(false)}
            />

            </>)}

        </div>
    );
};
export default SalesAdvisorPage;
